import {
  SelectedService,
  ServiceSchedule,
} from "../../../../../../types/availability";
import { CalendarEvent } from "../../../../../../types/transactions";

/** Minimal shape needed to resolve a service's calendar color. */
interface SalonServiceColorSource {
  id: string;
  default_calendar_event_color?: string;
}

/** Minimal client shape needed to label the transient event. */
interface TransientClient {
  id?: string;
  first_name?: string;
  last_name?: string;
}

const DEFAULT_EVENT_COLOR = "#36AFC9";

/**
 * Total scheduled minutes for a service (all duration segments + any
 * form-option adjustment delta), mirroring the sum used in ConfigureServicesStep.
 */
export const getServiceTotalDuration = (service: SelectedService): number =>
  service.baseDuration +
  service.processingTime +
  service.finishingTime +
  service.bufferTime +
  service.totalDurationDelta;

/**
 * Read a calendar Date's browser-local wall-clock as the `{date, time}` strings
 * a ServiceSchedule stores. The calendar renders salon wall-clock as
 * browser-local Dates (see mapBookedServiceToEvent / getSalonNow), so the local
 * components ARE the salon wall-clock. Using native getters (not moment) keeps
 * this correct regardless of any global moment default timezone. Shared by the
 * slot-click and drag-drop handlers.
 */
export const wallClockParts = (d: Date): { date: string; time: string } => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

/**
 * Build the "proposed" (transient) calendar events for the rebooking scheduling
 * screen — one per service that already has a chosen date/time. These render as
 * a dashed outline (see makeEventPropGetter + EventBlock.module.css)
 * and are never written into CalendarEventContext; they only exist for the
 * duration of the scheduling step.
 *
 * The schedule's date/time are already stored as salon wall-clock (they come
 * from reading the calendar slot's browser-local wall-clock, which equals the
 * salon wall-clock — see mapBookedServiceToEvent / getSalonNow). We build the
 * event Date from those parts in browser-local time (the same convention
 * mapBookedServiceToEvent uses via `new Date("YYYY-MM-DD HH:mm")`) so the
 * transient event lands on exactly the clicked slot regardless of any global
 * moment default timezone.
 */
export const buildTransientEvents = (
  selectedServices: SelectedService[],
  serviceSchedules: Record<string, ServiceSchedule>,
  activeServiceId: string | null,
  salonServices: SalonServiceColorSource[] | undefined,
  client: TransientClient | null,
): CalendarEvent[] => {
  const firstName = client?.first_name || "";
  const lastName = client?.last_name || "";
  const clientUuid = client?.id || "";

  return selectedServices.reduce<CalendarEvent[]>((events, service) => {
    const schedule = serviceSchedules[service.id];
    if (!schedule) {
      return events;
    }

    const totalDuration = getServiceTotalDuration(service);
    const [year, month, day] = schedule.date.split("-").map(Number);
    const [hour, minute] = schedule.time.split(":").map(Number);
    const start = new Date(year, month - 1, day, hour, minute);
    const end = new Date(start.getTime() + totalDuration * 60000);

    const backgroundColor =
      salonServices?.find((s) => s.id === service.salonServiceId)
        ?.default_calendar_event_color || DEFAULT_EVENT_COLOR;

    events.push({
      id: `transient-${service.id}`,
      title: `${service.serviceName} - ${firstName} ${lastName}`.trim(),
      start,
      end,
      backgroundColor,
      duration: totalDuration,
      baseDuration: service.baseDuration + service.totalDurationDelta,
      processingTime: service.processingTime,
      finishingTime: service.finishingTime,
      bufferTime: service.bufferTime,
      enableDoubleBooking: false,
      price: service.price,
      firstName,
      lastName,
      phone: "",
      email: "",
      salonServiceId: service.salonServiceId,
      employeeName: service.staffName,
      employeeId: service.staffId,
      resourceId: service.staffId,
      status: "",
      customerNotes: "",
      isOptedInReminder: false,
      clientUuid,
      transactionId: "",
      transactionStatus: "",
      payments: [],
      isTransient: true,
      isActiveTransient: service.id === activeServiceId,
    });

    return events;
  }, []);
};
